import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtNumber, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useSort, SortTh } from "@/components/sortable";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldWarning, ArrowsClockwise, CircleNotch, ClockCounterClockwise,
  WarningCircle, CheckCircle, Gear, ArrowClockwise,
} from "@phosphor-icons/react";

const ESTADO = {
  ok: { label: "Íntegro", color: "#065F46", bg: "#ECFDF5", border: "#A7F3D0", icon: ShieldCheck },
  advertencia: { label: "Advertencia", color: "#9A3412", bg: "#FFF7ED", border: "#FED7AA", icon: ShieldWarning },
  critico: { label: "Crítico", color: "#991B1B", bg: "#FEF2F2", border: "#FECACA", icon: WarningCircle },
};

function Metric({ label, value, sub, color = "#0F172A" }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</p>
      <p className="text-2xl font-black tracking-tight font-heading mt-1" style={{ color }}>{value}</p>
      {sub && <p className="text-xs text-[#64748B] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Integridad() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [audit, setAudit] = useState([]);
  const [params, setParams] = useState(null);
  const [pctInput, setPctInput] = useState("");
  const [skusInput, setSkusInput] = useState("");
  const [recalcLote, setRecalcLote] = useState(null);
  const [busyLote, setBusyLote] = useState(null);

  const loadReport = useCallback(() => {
    setLoading(true);
    api.get("/inventario/integridad").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const loadAux = useCallback(() => {
    api.get("/inventario/integridad/runs?limit=20").then((r) => setRuns(r.data));
    api.get("/inventario/auditoria?limit=50").then((r) => setAudit(r.data));
    api.get("/parametros").then((r) => {
      setParams(r.data);
      setPctInput(String(r.data.integridad_umbral_pct));
      setSkusInput(String(r.data.integridad_umbral_skus));
    });
  }, []);

  useEffect(() => { loadReport(); loadAux(); }, [loadReport, loadAux]);

  const ejecutar = async () => {
    setRunning(true);
    try {
      const r = await api.post("/inventario/integridad/run");
      setData(r.data);
      loadAux();
      toast.success(`Validación ejecutada · ${r.data.num_lotes_afectados} diferencia(s)`);
    } catch { toast.error("No se pudo ejecutar la validación"); }
    finally { setRunning(false); }
  };

  const confirmarRecalc = async () => {
    const lid = recalcLote?.lote_id;
    setBusyLote(lid);
    setRecalcLote(null);
    try {
      await api.post("/inventario/reconciliar/recalcular", { lote_id: lid });
      toast.success("Caché recalculado desde el ledger");
      loadReport(); loadAux();
    } catch { toast.error("No se pudo recalcular"); }
    finally { setBusyLote(null); }
  };

  const guardarParams = async () => {
    try {
      const r = await api.put("/parametros", {
        integridad_umbral_pct: parseFloat(pctInput),
        integridad_umbral_skus: parseInt(skusInput, 10),
      });
      setParams(r.data);
      loadReport(); loadAux();
      toast.success("Parámetros actualizados");
    } catch { toast.error("No se pudieron guardar los parámetros"); }
  };

  const { sorted, sortKey, sortDir, toggleSort } = useSort(data?.rows || []);
  const est = data ? ESTADO[data.estado] || ESTADO.ok : ESTADO.ok;
  const EstIcon = est.icon;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="integridad-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">
            Integridad de inventario
          </h1>
          <p className="text-[#475569] mt-1">
            Compara el stock del sistema contra el reconstruido desde el ledger inmutable. Solo detecta e informa.
          </p>
        </div>
        <Button data-testid="ejecutar-validacion-btn" onClick={ejecutar} disabled={running}
          className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
          {running ? <CircleNotch size={18} className="mr-1.5 animate-spin" /> : <ArrowClockwise size={18} className="mr-1.5" />}
          Ejecutar validación
        </Button>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-32">
          <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
        </div>
      ) : (
        <>
          {/* Banner estado */}
          <div className="rounded-md p-4 flex items-center gap-3 border"
            style={{ backgroundColor: est.bg, borderColor: est.border }} data-testid="integridad-banner">
            <EstIcon size={24} weight="bold" style={{ color: est.color }} />
            <div>
              <p className="font-bold" style={{ color: est.color }}>
                {data.estado === "ok"
                  ? "Inventario íntegro: el stock del sistema coincide con el ledger."
                  : data.estado === "critico"
                    ? "Alerta crítica: las diferencias superan el umbral configurado."
                    : "Se detectaron diferencias dentro del umbral tolerado."}
              </p>
              <p className="text-xs" style={{ color: est.color }}>
                Umbral: {data.umbral_pct}% del valor · {data.umbral_skus} SKUs · Última validación: {fmtDateTime(data.checked_at)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric label="Integridad" value={`${fmtNumber(data.integridad_pct)}%`} sub="score de cuadratura"
              color={est.color} />
            <Metric label="Valor en diferencia" value={fmtMoney(data.valor_diferencia)}
              sub={`${fmtNumber(data.pct_diferencia)}% del inventario`} color={data.valor_diferencia > 0 ? "#991B1B" : "#0F172A"} />
            <Metric label="Lotes afectados" value={fmtNumber(data.num_lotes_afectados)} sub="con diferencia" />
            <Metric label="SKUs afectados" value={fmtNumber(data.num_skus_afectados)} sub="productos distintos" />
          </div>

          <Tabs defaultValue="diferencias" className="space-y-4">
            <TabsList data-testid="integridad-tabs">
              <TabsTrigger value="diferencias" data-testid="tab-diferencias">Diferencias</TabsTrigger>
              <TabsTrigger value="ejecuciones" data-testid="tab-ejecuciones">Historial</TabsTrigger>
              <TabsTrigger value="auditoria" data-testid="tab-auditoria">Auditoría</TabsTrigger>
              <TabsTrigger value="parametros" data-testid="tab-parametros">Parámetros</TabsTrigger>
            </TabsList>

            {/* Diferencias */}
            <TabsContent value="diferencias">
              <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
                {sorted.length === 0 ? (
                  <div className="px-6 py-16 text-center">
                    <CheckCircle size={40} weight="bold" className="text-[#065F46] mx-auto mb-3" />
                    <p className="text-[#64748B]">Sin diferencias. El stock del sistema cuadra con el ledger.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="diferencias-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                          <SortTh label="SKU" sortKey="sku" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                          <SortTh label="Producto" sortKey="producto" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                          <SortTh label="Lote" sortKey="numero_lote" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                          <SortTh label="Sistema" sortKey="stock_sistema" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                          <SortTh label="Ledger" sortKey="stock_ledger" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                          <SortTh label="Diferencia" sortKey="diferencia" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                          <SortTh label="Valor dif." sortKey="valor_diferencia" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
                          <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((r) => (
                          <tr key={r.lote_id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors"
                            data-testid={`dif-row-${r.lote_id}`}>
                            <td className="px-5 py-3 font-mono text-xs text-[#475569]">{r.sku}</td>
                            <td className="px-5 py-3 font-semibold text-[#0F172A]">{r.producto}</td>
                            <td className="px-5 py-3 text-[#475569]">{r.numero_lote}</td>
                            <td className="px-5 py-3 text-right text-[#0F172A]">{fmtNumber(r.stock_sistema)}</td>
                            <td className="px-5 py-3 text-right text-[#0F172A]">{fmtNumber(r.stock_ledger)}</td>
                            <td className={`px-5 py-3 text-right font-bold ${r.diferencia > 0 ? "text-[#9A3412]" : "text-[#991B1B]"}`}>
                              {r.diferencia > 0 ? "+" : ""}{fmtNumber(r.diferencia)}
                            </td>
                            <td className="px-5 py-3 text-right font-semibold text-[#991B1B]">{fmtMoney(r.valor_diferencia)}</td>
                            <td className="px-5 py-3 text-right">
                              <Button size="sm" variant="outline" className="border-[#E2E8F0] h-8"
                                data-testid={`recalcular-btn-${r.lote_id}`}
                                disabled={busyLote === r.lote_id}
                                onClick={() => setRecalcLote(r)}>
                                {busyLote === r.lote_id
                                  ? <CircleNotch size={14} className="animate-spin" />
                                  : <><ArrowsClockwise size={14} className="mr-1" /> Recalcular</>}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Historial de ejecuciones */}
            <TabsContent value="ejecuciones">
              <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
                {runs.length === 0 ? (
                  <p className="px-6 py-12 text-center text-[#64748B]">Aún no hay ejecuciones registradas.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="runs-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                          <th className="px-5 py-3 font-semibold">Fecha</th>
                          <th className="px-5 py-3 font-semibold">Origen</th>
                          <th className="px-5 py-3 font-semibold">Estado</th>
                          <th className="px-5 py-3 font-semibold text-right">Integridad</th>
                          <th className="px-5 py-3 font-semibold text-right">Dif.</th>
                          <th className="px-5 py-3 font-semibold text-right">Valor dif.</th>
                          <th className="px-5 py-3 font-semibold text-right">Duración</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((r) => {
                          const e = ESTADO[r.estado] || ESTADO.ok;
                          return (
                            <tr key={r.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA]">
                              <td className="px-5 py-3 text-[#0F172A]">{fmtDateTime(r.fecha)}</td>
                              <td className="px-5 py-3 text-[#475569] capitalize">{r.trigger}</td>
                              <td className="px-5 py-3">
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold"
                                  style={{ color: e.color, backgroundColor: e.bg }}>{e.label}</span>
                              </td>
                              <td className="px-5 py-3 text-right">{fmtNumber(r.integridad_pct)}%</td>
                              <td className="px-5 py-3 text-right">{fmtNumber(r.num_lotes_afectados)}</td>
                              <td className="px-5 py-3 text-right">{fmtMoney(r.valor_diferencia)}</td>
                              <td className="px-5 py-3 text-right text-[#64748B]">{r.duracion_ms} ms</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Auditoría */}
            <TabsContent value="auditoria">
              <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
                {audit.length === 0 ? (
                  <p className="px-6 py-12 text-center text-[#64748B]">Sin registros de auditoría.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="auditoria-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                          <th className="px-5 py-3 font-semibold">Fecha</th>
                          <th className="px-5 py-3 font-semibold">Tipo</th>
                          <th className="px-5 py-3 font-semibold">Acción</th>
                          <th className="px-5 py-3 font-semibold">Detalle</th>
                          <th className="px-5 py-3 font-semibold">Usuario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {audit.map((a) => (
                          <tr key={a.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA]">
                            <td className="px-5 py-3 text-[#0F172A] whitespace-nowrap">{fmtDateTime(a.fecha)}</td>
                            <td className="px-5 py-3 text-[#475569] capitalize">{a.tipo}</td>
                            <td className="px-5 py-3 text-[#475569]">{a.accion}</td>
                            <td className="px-5 py-3 text-[#475569]">
                              {a.tipo === "parametro"
                                ? `${a.campo}: ${a.valor_anterior} → ${a.valor_nuevo}`
                                : a.tipo === "reconciliacion"
                                  ? `${a.numero_lote}: ${a.valor_anterior} → ${a.valor_nuevo}`
                                  : `${a.numero_lote} (${a.sku}) · dif ${a.diferencia} · ${fmtMoney(a.valor_diferencia)}`}
                            </td>
                            <td className="px-5 py-3 text-[#64748B]">{a.usuario}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Parámetros */}
            <TabsContent value="parametros">
              <div className="bg-white border border-[#E2E8F0] rounded-md p-6 max-w-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Gear size={20} weight="bold" className="text-[#0F172A]" />
                  <h2 className="font-heading text-lg font-bold text-[#0F172A]">Parámetros del sistema</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#0F172A]">Umbral de valor (% del inventario)</label>
                    <p className="text-xs text-[#64748B] mb-1.5">Sobre este porcentaje de diferencia se marca alerta crítica.</p>
                    <Input data-testid="param-pct-input" type="number" step="0.01" min="0" value={pctInput}
                      onChange={(e) => setPctInput(e.target.value)} className="w-40 border-[#E2E8F0]" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#0F172A]">Umbral de SKUs afectados</label>
                    <p className="text-xs text-[#64748B] mb-1.5">Sobre esta cantidad de SKUs con diferencia se marca alerta crítica.</p>
                    <Input data-testid="param-skus-input" type="number" step="1" min="0" value={skusInput}
                      onChange={(e) => setSkusInput(e.target.value)} className="w-40 border-[#E2E8F0]" />
                  </div>
                  <Button data-testid="guardar-params-btn" onClick={guardarParams}
                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white">Guardar parámetros</Button>
                  <p className="text-xs text-[#94A3B8] flex items-center gap-1 pt-2">
                    <ClockCounterClockwise size={13} /> Todo cambio queda registrado en la auditoría.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Diálogo confirmación recalcular */}
      <Dialog open={!!recalcLote} onOpenChange={(v) => !v && setRecalcLote(null)}>
        <DialogContent data-testid="recalcular-dialog">
          <DialogHeader>
            <DialogTitle>Recalcular caché desde el ledger</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#475569] space-y-2">
            <p>
              Esta es una <b>corrección técnica</b>: reconstruye <code>stock_actual</code> a partir del ledger
              inmutable. <b>No</b> crea movimientos ni altera el historial.
            </p>
            {recalcLote && (
              <div className="bg-[#F8F9FA] rounded-md p-3 border border-[#E2E8F0]">
                <p className="font-semibold text-[#0F172A]">{recalcLote.producto} · {recalcLote.numero_lote}</p>
                <p>Sistema: {fmtNumber(recalcLote.stock_sistema)} → Ledger: {fmtNumber(recalcLote.stock_ledger)}</p>
              </div>
            )}
            <p className="text-xs text-[#9A3412]">
              Si un conteo físico demuestra que el stock real difiere del ledger, usa en su lugar
              un <b>ajuste de inventario</b> (Movimientos → Ajuste), que registra el hecho de negocio en el ledger.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#E2E8F0]" onClick={() => setRecalcLote(null)}>Cancelar</Button>
            <Button data-testid="confirmar-recalcular-btn" onClick={confirmarRecalc}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white">Recalcular caché</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
